import dayjs from 'dayjs'
import React, { FC, useMemo } from 'react'
import DoneIcon from '@mui/icons-material/Done'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Box, Checkbox, Chip, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
// components
import { StickyTable } from '../../common/StickyTable'
import { PayableRowActions } from './PayablesActionMenu'
import { payableStatusColors } from '../../../utils/billing'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { CustomTableCell } from '../../common/CustomTableCell'
import { CustomPagination } from '../../common/CustomPagination'
// others
import { AccountsPayableListingProps } from '../../../interfaceTypes/props'
import { enumToLabel, formatCurrency, getProperPaymentMethod } from '../../../utils'
import { ACCOUNT_PAYABLES_HEADERS, FILTER_DATE_FORMAT, NOT_APPLICABLE, QUERY_PARAMS } from '../../../constants'

const AccountsPayableListing: FC<AccountsPayableListingProps> = ({
  loading,
  payables,
  onEdit,
  onVoid,
  onVoidAndDuplicate,
  onDelete,
  onPreviewCheck,
  onPrintCheck,
  previewingVoucher
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // The selection is owned by the URL, not by local state — the parent both reads it (to drive the
  // check-printing actions) and clears it when the filters change, so deriving it here keeps the
  // checkboxes in step with those resets instead of holding a stale copy.
  const selectedParam = searchParams.get(QUERY_PARAMS.SELECTED_PAYABLES)
  const isAllSelected = selectedParam === QUERY_PARAMS.ALL
  const selectedPayables = useMemo(
    () =>
      isAllSelected ? payables.map(payable => payable.voucherNo) : selectedParam?.split(',').filter(Boolean) ?? [],
    [isAllSelected, selectedParam, payables]
  )
  const selectAll = isAllSelected || (payables.length > 0 && selectedPayables.length === payables.length)

  const updateSelection = (selection: string[]) => {
    const next = new URLSearchParams(searchParams)
    if (!selection.length) next.delete(QUERY_PARAMS.SELECTED_PAYABLES)
    else if (selection.length === payables?.length) next.set(QUERY_PARAMS.SELECTED_PAYABLES, QUERY_PARAMS.ALL)
    else next.set(QUERY_PARAMS.SELECTED_PAYABLES, selection.join(','))
    setSearchParams(next)
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) =>
    updateSelection(event.target.checked ? payables?.map(payable => payable.voucherNo) || [] : [])

  const handleRowSelect = (payableVoucher: string) =>
    updateSelection(
      selectedPayables.includes(payableVoucher)
        ? selectedPayables.filter(voucherNo => voucherNo !== payableVoucher)
        : [...selectedPayables, payableVoucher]
    )

  return (
    <>
      <StickyTable pagination={<CustomPagination loading={loading || false} />} height="600px">
        <TableHead>
          <TableRow hover>
            <TableCell>
              <Checkbox checked={selectAll} onChange={handleSelectAll} size="small" />
            </TableCell>

            {ACCOUNT_PAYABLES_HEADERS.map((columnName, index) => (
              <TableCell key={index} component="th" align={'left'}>
                {columnName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {loading ? (
          <TableSkeleton noOfRows={7} noOfCols={ACCOUNT_PAYABLES_HEADERS.length} />
        ) : (
          <TableBody>
            {payables.map(payable => {
              const isSelected = selectedPayables.includes(payable.voucherNo)
              const matter = payable.lineItems?.find(item => item.litigation)?.litigation
              const glAccount = payable.lineItems?.find(item => item.glAccount)?.glAccount

              return (
                <TableRow key={payable.voucherNo}>
                  <TableCell>
                    <Checkbox checked={isSelected} onChange={() => handleRowSelect(payable.voucherNo)} size="small" />
                  </TableCell>
                  <CustomTableCell value={payable.voucherNo} to={`${payable.voucherNo}`} />
                  <CustomTableCell
                    value={`(${payable?.vendor?.vendorId || 'N/A'}) ${payable.vendor?.name || ''}`}
                    noWrap={false}
                  />
                  <CustomTableCell
                    value={matter ? `#${matter?.caseNumber ?? matter?.consultMatterNumber ?? ''}` : ''}
                    to={matter ? `/matters/matter-detail/${matter?.id}` : undefined}
                  />
                  <TableCell>
                    <Typography variant="caption">{dayjs(payable.payDate).format(FILTER_DATE_FORMAT)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{dayjs(payable.invoiceDate).format(FILTER_DATE_FORMAT)}</Typography>
                  </TableCell>
                  <CustomTableCell value={getProperPaymentMethod(payable.paymentMethod)} />
                  <CustomTableCell value={payable.checkOrAchNumber || '--'} />
                  {/* Sort code only — it is what staff identify a bank account by, and the full name
                      ("WPB New Operating Account") would crowd out the columns either side of it. */}
                  <CustomTableCell
                    value={payable.account?.sortCode || NOT_APPLICABLE}
                    to={payable.account?.id && `/accounts/chartered/${payable.account.id}`}
                  />
                  <CustomTableCell
                    value={`${glAccount?.sortCode ? `(${glAccount.sortCode}) ` : ''}${
                      glAccount?.name || NOT_APPLICABLE
                    }`}
                    to={glAccount?.id && `/accounts/chartered/${glAccount.id}`}
                    noWrap={false}
                  />
                  <TableCell>
                    <Chip
                      label={enumToLabel(payable.status)}
                      sx={{
                        color: payableStatusColors[payable.status],
                        border: `1px solid ${payableStatusColors[payable.status]}50`,
                        backgroundColor: 'transparent'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    {payable.printedAt ? (
                      <Box>
                        <Typography variant="caption" display="block" lineHeight={1.2}>
                          {dayjs(payable.printedAt).format('MM/DD/YYYY')}
                        </Typography>
                        <Typography variant="caption" display="block" lineHeight={1.2} color="text.secondary">
                          {dayjs(payable.printedAt).format('hh:mm A')}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption">--</Typography>
                    )}
                  </TableCell>
                  <CustomTableCell value={formatCurrency(payable.amount)} />
                  <TableCell>
                    {payable.isReconciled ? (
                      <Tooltip
                        title={
                          payable.reconciledAt
                            ? `Reconciled against the ${dayjs(payable.reconciledAt).format(
                                FILTER_DATE_FORMAT
                              )} statement`
                            : 'Reconciled'
                        }
                      >
                        <DoneIcon fontSize="small" color="success" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption">{'-'}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <PayableRowActions
                      payable={payable}
                      isPreviewing={previewingVoucher === payable.voucherNo}
                      onView={() => navigate(`${payable.voucherNo}`)}
                      onEdit={() => onEdit(payable)}
                      onDelete={() => onDelete(payable)}
                      onVoid={() => onVoid(payable)}
                      onVoidAndDuplicate={() => onVoidAndDuplicate(payable)}
                      onPreviewCheck={() => onPreviewCheck(payable)}
                      onPrintCheck={() => onPrintCheck(payable)}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        )}
      </StickyTable>
    </>
  )
}

export default AccountsPayableListing
